// DAS-2181 Phase 3: Data Section — Positions table (left) + News feed & Performance summary (right)
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { fetchNews } from '../helpers/portfolioAnalysisHelper';
import { ANNOTATION_REGIONS } from './terminalZoneConfig';

const TerminalDataSection = ({
  data = [],
  exposures = [],
  tpData = {},
  yfData = {},
  benchmarkData = null,
  stockTickers = [],
  onCardClick,
  annotationMode,
  onAnnotationClick,
  annotations = [],
  source,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [newsData, setNewsData] = useState({});
  const [newsLoading, setNewsLoading] = useState(false);
  const fetchedRef = useRef(false);

  // Fetch news once
  useEffect(() => {
    if (fetchedRef.current || !stockTickers || stockTickers.length === 0) return;
    fetchedRef.current = true;
    setNewsLoading(true);
    fetchNews(stockTickers)
      .then(result => setNewsData(result.news || {}))
      .catch(() => {})
      .finally(() => setNewsLoading(false));
  }, [stockTickers]);

  // Build positions table rows sorted by weight
  const positions = useMemo(() => {
    const portfolioTotal = data.reduce((s, d) => s + (parseFloat(d.Value_eur) || 0), 0);
    const expMap = {};
    exposures.forEach(e => { expMap[(e.ticker || '').toUpperCase()] = parseFloat(e.total_amount_exposure) || 0; });

    return data
      .filter(d => d.Ticker)
      .map(d => {
        const ticker = (d.Ticker || '').toUpperCase();
        const val = expMap[ticker] || parseFloat(d.Value_eur) || 0;
        const weight = portfolioTotal > 0 ? (val / portfolioTotal * 100) : 0;
        const tp = tpData[ticker] || {};
        const yf = yfData[ticker] || {};

        const ret1m = tp.one_month_performance != null ? parseFloat(tp.one_month_performance) : null;
        const momentum = tp.select_now_momentum != null ? parseFloat(tp.select_now_momentum) : null;
        const score = tp.fundamental_final_score != null ? parseFloat(tp.fundamental_final_score) : null;
        const msci = yf.MSCI_ESG_Rating || tp.MSCI_ESG_Rating || null;
        const hasAlert = tp.Likely_Institutional_Entry === 1 || tp.Likely_Institutional_Exit === 1;

        return {
          ticker,
          name: d.Company || yf.shortName || '',
          weight,
          ret1m,
          momentum,
          score,
          msci,
          hasAlert,
        };
      })
      .sort((a, b) => {
        // Positions with market data first, then by weight within each group
        const aHasData = a.ret1m != null || a.score != null ? 1 : 0;
        const bHasData = b.ret1m != null || b.score != null ? 1 : 0;
        if (bHasData !== aHasData) return bHasData - aHasData;
        return b.weight - a.weight;
      });
  }, [data, exposures, tpData, yfData]);

  // News rows — most recent first, limit to 6
  const newsRows = useMemo(() => {
    return Object.entries(newsData)
      .map(([ticker, n]) => ({
        ticker,
        title: n.article_title || '',
        date: n.date || '',
        url: n.article_url || '',
        impact: (n.impact_type || '').toLowerCase(),
      }))
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
      .slice(0, 6);
  }, [newsData]);

  // Performance stats
  const perfStats = useMemo(() => {
    const total = positions.length;
    if (total === 0) return null;

    const with1m = positions.filter(p => p.ret1m != null);
    const positive1m = with1m.filter(p => p.ret1m > 0).length;
    const positivePct = with1m.length > 0 ? Math.round(positive1m / with1m.length * 100) : 0;

    // Benchmark comparison (SPY 1M)
    const bench1m = benchmarkData?.returns?.['1M'] != null ? parseFloat(benchmarkData.returns['1M']) : null;
    const exceedingBench = bench1m != null
      ? with1m.filter(p => p.ret1m > bench1m).length
      : 0;
    const exceedPct = bench1m != null && with1m.length > 0
      ? Math.round(exceedingBench / with1m.length * 100)
      : null;

    // Portfolio weighted 1M return
    const totalWeight = positions.reduce((s, p) => s + p.weight, 0);
    const portf1m = totalWeight > 0
      ? positions.filter(p => p.ret1m != null).reduce((s, p) => s + p.ret1m * p.weight, 0) / totalWeight
      : null;

    const alpha = portf1m != null && bench1m != null ? portf1m - bench1m : null;

    return {
      positive1m, positiveTotal: with1m.length, positivePct,
      exceedingBench, exceedPct,
      portf1m, bench1m, alpha,
    };
  }, [positions, benchmarkData]);

  const visiblePositions = expanded ? positions : positions.slice(0, 8);
  const remaining = positions.length - 8;

  const fmtPct = (v) => v > 0 ? `+${v.toFixed(1)}%` : `${v.toFixed(1)}%`;
  const pctClass = (v) => v > 0 ? 'up' : v < 0 ? 'dn' : 'neu';

  const esgColor = (grade) => {
    if (!grade) return undefined;
    const g = grade.toUpperCase();
    if (['AAA', 'AA', 'A'].includes(g)) return '#3daa6e';
    if (['BBB', 'BB'].includes(g)) return '#c8852a';
    return '#e05555';
  };

  const newsTickerColor = (impact) => {
    if (impact === 'positive') return '#3daa6e';
    if (impact === 'negative') return '#e05555';
    return '#c8852a';
  };

  const handleTableClick = () => {
    if (annotationMode) {
      onAnnotationClick?.(ANNOTATION_REGIONS.table(), 'Positions Table');
    }
  };

  const handleNewsClick = () => {
    if (annotationMode) {
      onAnnotationClick?.(ANNOTATION_REGIONS.sidebar('news_feed'), 'News Feed');
    } else {
      onCardClick?.('news');
    }
  };

  const handlePerfClick = () => {
    if (annotationMode) {
      onAnnotationClick?.(ANNOTATION_REGIONS.sidebar('performance'), 'Performance Summary');
    } else {
      onCardClick?.('performance');
    }
  };

  return (
    <div className="data-section">
      {/* Left: Positions table */}
      <div className={`ds-left${annotationMode ? ' annotatable annotation-mode' : ''}`} onClick={handleTableClick}>
        <div className="tbl-head">
          <span>ticker</span>
          <span>name</span>
          <span>weight</span>
          <span>1M ret.</span>
          <span>momentum</span>
          <span>score</span>
          <span>ESG</span>
          <span style={{ textAlign: 'center' }}>{'\u26A0'}</span>
        </div>
        {visiblePositions.map(p => (
          <div className="tbl-row" key={p.ticker} onClick={(e) => {
            if (!annotationMode) { e.stopPropagation(); onCardClick?.('portfolio'); }
          }}>
            <span className="ticker">{p.ticker}</span>
            <span className="name">{p.name}</span>
            <span>{p.weight.toFixed(2)}%</span>
            <span className={p.ret1m != null ? pctClass(p.ret1m) : 'neu'}>
              {p.ret1m != null ? fmtPct(p.ret1m) : '—'}
            </span>
            <span className={p.momentum != null ? pctClass(p.momentum) : 'neu'}>
              {p.momentum != null ? fmtPct(p.momentum) : '—'}
            </span>
            <span className={p.score != null ? (p.score >= 60 ? 'up' : p.score >= 40 ? 'warn' : 'dn') : 'neu'}>
              {p.score != null ? Math.round(p.score) : '—'}
            </span>
            <span className="esg-tag" style={{ color: esgColor(p.msci) }}>
              {p.msci || '—'}
            </span>
            <span>{p.hasAlert ? <span className="alert-dot" /> : ''}</span>
          </div>
        ))}
        {!expanded && remaining > 0 && (
          <div className="tbl-more" onClick={(e) => { e.stopPropagation(); setExpanded(true); }}>
            {'\u2193'} {remaining} more positions — click to expand
          </div>
        )}
        {expanded && positions.length > 8 && (
          <div className="tbl-more" onClick={(e) => { e.stopPropagation(); setExpanded(false); }}>
            {'\u2191'} collapse
          </div>
        )}
      </div>

      {/* Right: News + Performance */}
      <div className="side-panels">
        {/* News Feed */}
        <div className={`side-panel${annotationMode ? ' annotatable annotation-mode' : ''}`}>
          <div className="p-head" onClick={handleNewsClick}>
            <span className="p-title">news feed</span>
            <span style={{ fontSize: 9, color: '#5a6a80' }}>trendsearch {'\u00B7'} live</span>
          </div>
          {newsLoading && <div className="news-loading">loading news...</div>}
          {!newsLoading && newsRows.length === 0 && (
            <div className="news-loading">no recent news for portfolio tickers</div>
          )}
          {newsRows.map((n, i) => (
            <div className="news-row" key={i} onClick={() => n.url && window.open(n.url, '_blank')}>
              <div className="news-top">
                <span className="news-ticker" style={{ color: newsTickerColor(n.impact) }}>{n.ticker}</span>
                <span className="news-time">{n.date ? new Date(n.date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
              </div>
              <div className="news-text">{n.title}</div>
            </div>
          ))}
        </div>

        {/* Performance Summary */}
        <div className={`side-panel${annotationMode ? ' annotatable annotation-mode' : ''}`}>
          <div className="p-head" onClick={handlePerfClick}>
            <span className="p-title">performance</span>
            {perfStats && perfStats.positivePct >= 50 && (
              <span className="p-badge ok">on track</span>
            )}
            {perfStats && perfStats.positivePct < 50 && (
              <span className="p-badge warn">under pressure</span>
            )}
          </div>
          {perfStats && (
            <div style={{ padding: '8px 0' }}>
              <div className="perf-bar-wrap">
                <div className="perf-bar-label">
                  <span className="lbl">positive performance</span>
                  <span className="up">{perfStats.positivePct}% <span className="count">({perfStats.positive1m}/{perfStats.positiveTotal})</span></span>
                </div>
                <div className="perf-bar-track">
                  <div className="perf-bar-fill" style={{ width: `${perfStats.positivePct}%`, background: '#3daa6e' }} />
                </div>
              </div>
              {perfStats.exceedPct != null && (
                <div className="perf-bar-wrap" style={{ marginTop: 6 }}>
                  <div className="perf-bar-label">
                    <span className="lbl">exceeding benchmark</span>
                    <span style={{ color: '#4a90c4' }}>{perfStats.exceedPct}% <span className="count">({perfStats.exceedingBench}/{perfStats.positiveTotal})</span></span>
                  </div>
                  <div className="perf-bar-track">
                    <div className="perf-bar-fill" style={{ width: `${perfStats.exceedPct}%`, background: '#4a90c4' }} />
                  </div>
                </div>
              )}
              <div className="p-divider" style={{ margin: '8px 0' }} />
              <div className="perf-vs">
                <div className="perf-vs-item">
                  <span className="perf-vs-label">portfolio 1M</span>
                  <span className={perfStats.portf1m != null ? pctClass(perfStats.portf1m) : ''} style={{ fontSize: 13 }}>
                    {perfStats.portf1m != null ? fmtPct(perfStats.portf1m) : '—'}
                  </span>
                </div>
                <div className="perf-vs-item">
                  <span className="perf-vs-label">SPY benchmark</span>
                  <span className={perfStats.bench1m != null ? pctClass(perfStats.bench1m) : ''} style={{ fontSize: 13 }}>
                    {perfStats.bench1m != null ? fmtPct(perfStats.bench1m) : '—'}
                  </span>
                </div>
                <div className="perf-vs-item">
                  <span className="perf-vs-label">alpha</span>
                  <span className={perfStats.alpha != null ? pctClass(perfStats.alpha) : ''} style={{ fontSize: 13 }}>
                    {perfStats.alpha != null ? fmtPct(perfStats.alpha) : '—'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TerminalDataSection;
